import { Analysis, PossibleRootCauseCategory, ProbabilityCategory } from '.';

export class GlucoseAnalysis extends Analysis {
    glucoseRegulationProbability?: number;
    glucoseRegulationProbabilityCategory?: ProbabilityCategory;
    /**
     * TG/HDL is past the lab↔optimal midpoint and is the ONLY driver of the
     * reading: no pair fired, nothing else is out of lab, and the rest of the
     * panel is measured and optimal. The Insights panel offers the non-glycemic
     * differential on this state. Set here rather than recomputed by the panel so
     * the ladder stays the single source of truth for it.
     */
    tgHdlPastMidpointDespiteOptimalPanel?: boolean;

    reset() {
        super.reset();
        this.glucoseRegulationProbability = -1;
        this.glucoseRegulationProbabilityCategory = ProbabilityCategory.NA;
        this.tgHdlPastMidpointDespiteOptimalPanel = false;
    }

    performAnalysis() {
        super.performAnalysis();
        this.calculateGlucoseRegulationLikelihood();
        this.calculateGlucoseRegulationLikelihoodCategory();
    }

    calculateGlucoseRegulationLikelihood() {
        const ba = this.ba;
        const hasGlucose = ba.glucose?.hasValue?.();
        const hasHbA1c   = ba.hemoglobinA1c?.hasValue?.();
        const hasTgHdl   = ba.triglyceridesHDLRatio?.hasValue?.();
        const hasInsulin = ba.insulin?.hasValue?.();
        const hasCp      = ba.cpeptide?.hasValue?.();

        // Bail only when no glucose-related marker at all is present. Insulin
        // and C-peptide qualify now that the gauge surfaces an insulin-signal
        // tier (the early-warning insulin-resistance use case where Glucose
        // and HbA1c sit in range but Insulin is elevated).
        if (!hasGlucose && !hasHbA1c && !hasTgHdl && !hasInsulin && !hasCp) return;

        // Alarm boundary checks use >= alarmMax / <= alarmMin (inclusive at the
        // boundary), unlike the existing isGreaterThanAlarm/isLessThanAlarm
        // helpers which are strict. A Glucose value exactly equal to alarmMax
        // is treated as alarm-tier, not lab-tier — confirmed product decision.
        const geAlarmHigh = (m?: any): boolean =>
            !!m?.hasValue?.() && !!m?.range && m.value >= m.range.alarmMax;
        const leAlarmLow = (m?: any): boolean =>
            !!m?.hasValue?.() && !!m?.range && m.value <= m.range.alarmMin;

        const gLab    = hasGlucose && ba.glucose.isGreaterThanLab();
        const gLowLab = hasGlucose && ba.glucose.isLessThanLab();
        const gAlarm  = geAlarmHigh(ba.glucose);
        const gAlarmLow = leAlarmLow(ba.glucose);
        const gHigh   = hasGlucose && ba.glucose.isGreaterThanOptimal();
        const gLow    = hasGlucose && ba.glucose.isStricklyLessThanOptimal();

        const hLab    = hasHbA1c && ba.hemoglobinA1c.isGreaterThanLab();
        const hLowLab = hasHbA1c && ba.hemoglobinA1c.isLessThanLab();
        const hAlarm  = geAlarmHigh(ba.hemoglobinA1c);
        const hAlarmLow = leAlarmLow(ba.hemoglobinA1c);
        const hHigh   = hasHbA1c && ba.hemoglobinA1c.isGreaterThanOptimal();
        const hLow    = hasHbA1c && ba.hemoglobinA1c.isStricklyLessThanOptimal();

        const tLab   = hasTgHdl && ba.triglyceridesHDLRatio.isGreaterThanLab();
        const tAlarm = geAlarmHigh(ba.triglyceridesHDLRatio);
        const tHigh  = hasTgHdl && ba.triglyceridesHDLRatio.isGreaterThanOptimal();

        // Insulin and C-peptide collapse into a single "insulin-signal" per
        // Q2Y — both reflect endogenous insulin output, so the gauge counts
        // whichever is more extreme and surfaces a combined category rather
        // than two near-duplicate rows. Multi-marker tiers (99% / 97%) keep
        // counting Glucose / HbA1c / TG/HDL only — the insulin signal feeds
        // into 95% / 85% / 80% / 60% paths.
        const insulinLow = hasInsulin && ba.insulin.isStricklyLessThanOptimal();
        const cpLow      = hasCp && ba.cpeptide.isStricklyLessThanOptimal();
        const isAlarmHigh = geAlarmHigh(ba.insulin) || geAlarmHigh(ba.cpeptide);
        const isAlarmLow  = leAlarmLow(ba.insulin)  || leAlarmLow(ba.cpeptide);
        const isLabHigh = (hasInsulin && ba.insulin.isGreaterThanLab()) ||
                          (hasCp && ba.cpeptide.isGreaterThanLab());
        const isLabLow  = (hasInsulin && ba.insulin.isLessThanLab()) ||
                          (hasCp && ba.cpeptide.isLessThanLab());
        const isOptHigh = (hasInsulin && ba.insulin.isGreaterThanOptimal()) ||
                          (hasCp && ba.cpeptide.isGreaterThanOptimal());

        // How far past optimal each off-optimal marker sits, graded at the
        // lab↔optimal midpoint — the same midpoint the immune gauge uses
        // (avgMax = (optimalMax + labMax) / 2, avgMin = (optimalMin + labMin) / 2).
        // "past midpoint" = more than half-way from optimal toward the lab bound
        // (isGreaterThanAvgMax high / isLessThanAvgMin low); otherwise the marker
        // is only mildly off-optimal. This lets the above-optimal pair and single
        // tiers report a lower likelihood when the markers barely clear optimal
        // instead of a flat value regardless of magnitude.
        const gPastMid = hasGlucose && ba.glucose.isGreaterThanAvgMax();
        const hPastMid = hasHbA1c && ba.hemoglobinA1c.isGreaterThanAvgMax();
        // TG/HDL is the only primary marker without an above-lab tier of its own
        // (the 85% block covers Glucose, HbA1c and the insulin signal), so an
        // above-lab ratio lands here in the pair / single tiers. isGreaterThanAvgMax
        // is capped at lab max, which would grade a 4.5 ratio (lab max 4) as "only
        // mildly above optimal" — tLab counts as past the midpoint instead.
        const tPastMid = hasTgHdl && (ba.triglyceridesHDLRatio.isGreaterThanAvgMax() || tLab);
        const isPastMidHigh = (hasInsulin && ba.insulin.isGreaterThanAvgMax()) ||
                              (hasCp && ba.cpeptide.isGreaterThanAvgMax());
        const gLowPastMid = hasGlucose && ba.glucose.isLessThanAvgMin();
        const hLowPastMid = hasHbA1c && ba.hemoglobinA1c.isLessThanAvgMin();
        const insulinLowPastMid = hasInsulin && ba.insulin.isLessThanAvgMin();
        const cpLowPastMid = hasCp && ba.cpeptide.isLessThanAvgMin();

        // Pair likelihood, graded by how many of the two markers clear the
        // midpoint: 80% both past, 72% one past, 65% both only mildly off. Each
        // step routes to its OWN Interpretation category so the DB title's %
        // always matches the gauge (id80 keeps the original pattern id; id72 /
        // id65 are the graded siblings 71040-71049).
        type Candidate = { score: number; id: PossibleRootCauseCategory };
        const pairCandidates: Candidate[] = [];
        const singleCandidates: Candidate[] = [];
        /** Highest-scoring candidate; ties go to whichever was listed first. */
        const bestOf = (list: Candidate[]): Candidate | undefined =>
            list.reduce<Candidate | undefined>((best, c) => (!best || c.score > best.score ? c : best), undefined);
        const addPair = (
            active: boolean, a: boolean, b: boolean,
            id80: PossibleRootCauseCategory, id72: PossibleRootCauseCategory, id65: PossibleRootCauseCategory,
        ) => {
            if (!active) return;
            const n = (a ? 1 : 0) + (b ? 1 : 0);
            pairCandidates.push(n === 2 ? { score: 80, id: id80 } : n === 1 ? { score: 72, id: id72 } : { score: 65, id: id65 });
        };
        // Whether the rest of the panel actively argues against dysregulation.
        // Reassurance takes either an optimal insulin signal (the most direct
        // evidence the pancreas isn't compensating) or two other primary
        // markers measured and optimal. A marker that is itself off-optimal
        // never counts, so the firing marker contributes nothing here.
        const gOptimal = hasGlucose && !gHigh && !gLow;
        const hOptimal = hasHbA1c && !hHigh && !hLow;
        const tOptimal = hasTgHdl && !tHigh;
        const insulinSignalOptimal = (hasInsulin || hasCp) &&
            !isOptHigh && !insulinLow && !cpLow;
        const optimalCount = (gOptimal ? 1 : 0) + (hOptimal ? 1 : 0) + (tOptimal ? 1 : 0);
        const corroboratedOptimal = insulinSignalOptimal || optimalCount >= 2;

        // Single likelihood: 60% past the midpoint (original id), 55% only mildly
        // off-optimal (graded sibling 71050-71057). The step-down also requires
        // corroboration: on a thin panel (TG/HDL above optimal with HbA1c,
        // Insulin and C-peptide never ordered, say) nothing has ruled the pattern
        // out, so a mild single holds at 60%.
        // The mild floor is 55, not 50, for two reasons: a marker that is off
        // optimal at all is a real finding even when the rest of the panel looks
        // fine, and 50 lands exactly on the gauge's green/yellow band seam, so it
        // reads as Less Likely however the category is labeled.
        const addSingle = (
            active: boolean, pastMid: boolean,
            id60: PossibleRootCauseCategory, idMild: PossibleRootCauseCategory,
        ) => {
            if (!active) return;
            singleCandidates.push(pastMid || !corroboratedOptimal ? { score: 60, id: id60 } : { score: 55, id: idMild });
        };

        const highLabCount = (gLab ? 1 : 0) + (hLab ? 1 : 0) + (tLab ? 1 : 0);

        // First-match-wins ladder. Within a tier, lab-range singles outrank
        // above-optimal pairs, and the high side is evaluated before the low
        // side (mixed high+low cases land on the high path — they're rare and
        // both directions show up in the underlying clinical patterns
        // separately).

        // ── 99% Highly Likely ─────────────────────────────────────────────
        if (highLabCount === 3) {
            return this.setGaugeDriver(99, PossibleRootCauseCategory.CATEGORY_71099);
        }
        if (gLowLab && hLowLab) {
            return this.setGaugeDriver(99, PossibleRootCauseCategory.CATEGORY_71098);
        }

        // ── 97% Highly Likely — 2 of 3 above lab ──────────────────────────
        if (highLabCount === 2) {
            if (gLab && hLab) return this.setGaugeDriver(97, PossibleRootCauseCategory.CATEGORY_71097);
            if (gLab && tLab) return this.setGaugeDriver(97, PossibleRootCauseCategory.CATEGORY_71096);
            /* hLab && tLab */ return this.setGaugeDriver(97, PossibleRootCauseCategory.CATEGORY_71095);
        }

        // ── 95% Highly Likely — single ≥ alarm ────────────────────────────
        // Per the product decision in the alarm-gate review: Glucose first
        // (a markedly elevated fasting glucose is the most concerning single
        // finding), then HbA1c, then TG/HDL, then the combined insulin-signal,
        // then the low-side mirror in the same order.
        if (gAlarm)     return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71094);
        if (hAlarm)     return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71093);
        if (tAlarm)     return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71092);
        if (isAlarmHigh) return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71082);
        if (gAlarmLow)  return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71091);
        if (hAlarmLow)  return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71090);
        if (isAlarmLow) return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71081);

        // ── 85% Likely — single > lab but < alarm ─────────────────────────
        if (gLab)    return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71089);
        if (hLab)    return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71088);
        if (isLabHigh) return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71080);
        if (gLowLab) return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71087);
        if (hLowLab) return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71086);
        if (isLabLow) return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71079);

        // ── 75% Likely — TG/HDL above lab, on its own ─────────────────────
        // TG/HDL gets no rung in the 85% block because it is a lipid surrogate
        // rather than a direct glucose measure, so without this tier an above-lab
        // ratio would fall through to the 60% single and read lower than a lone
        // above-lab Glucose at 85%. Reaching here means TG/HDL is the only marker
        // out of lab (2-of-3 lands at 97%, its own alarm at 95%).
        // A partner that is above optimal AND past its own midpoint still earns
        // the 80% pair below; a merely mild partner would score 72%, which must
        // not undercut this rung, so it lands here instead. Only Glucose and
        // HbA1c count as that partner: they are the ones with a TG/HDL pair rung
        // below. The insulin signal has no TG/HDL pair, so counting it here would
        // skip this rung and drop the panel to the 60% TG/HDL single, i.e. adding
        // a compensating pancreas to an above-lab ratio would LOWER the gauge. An
        // insulin signal that is itself out of lab already outranks this rung at
        // 85% / 95% above.
        // Corroboration applies here too, and it matters more than anywhere else
        // in the ladder. TG/HDL is a lipid ratio, so an above-lab value alongside
        // an optimal Glucose, HbA1c and insulin signal is more likely explained by
        // something non-glycemic (alcohol, oral estrogen, thyroid, familial
        // hypertriglyceridemia, a non-fasting draw) than by glucose regulation the
        // three direct measures say is intact. That case falls through to the 60%
        // single below, which is what it literally is: TG/HDL above optimal, past
        // its midpoint. The 75% rung is for the unexplained ratio — nothing else
        // ordered, so nothing rules the pattern in or out.
        const partnerPastMid = (gHigh && gPastMid) || (hHigh && hPastMid);
        if (tLab && !partnerPastMid) {
            // Corroboration here needs the insulin signal to be optimal as well.
            // An insulin or C-peptide above optimal argues FOR dysregulation, so
            // two optimal primaries must not launder it into reassurance: that
            // would score a compensating pancreas exactly like an optimal one.
            if (!corroboratedOptimal || isOptHigh) {
                // Offered as a candidate rather than returned outright, so the pair
                // tier below can outrank it. TG/HDL + an insulin signal both past
                // their midpoints is an 80% pair, and an above-lab ratio must never
                // read LOWER than the same panel with the ratio merely past its
                // midpoint. On its own, or with only a mildly off partner, this is
                // still the 75% reading.
                pairCandidates.push({ score: 75, id: PossibleRootCauseCategory.CATEGORY_71078 });
            }
        }

        // ── 80% / 72% / 65% — above-optimal pair, graded by midpoint ───────
        // Flat 80% (Likely) when both markers clear the lab↔optimal midpoint;
        // steps down to 72% (Likely) when only one does and 65% (Possible) when
        // both are only mildly above optimal — the early-warning case where the
        // pattern is real but the markers barely leave the optimal band.
        // 71068 (Glucose + insulin-signal) and 71067 (HbA1c + insulin-signal)
        // catch the early-warning insulin-resistance pattern: pancreas is
        // already compensating but Glucose / HbA1c still in lab range.
        // This list is ordered by marker priority, NOT by severity, so returning
        // the FIRST match let a milder pair outrank a worse one that also applied:
        // HbA1c mild + TG/HDL past its midpoint read 72, and adding a mild Glucose
        // handed the reading to the Glucose+HbA1c pair at 65. Every applicable
        // pair is scored and the highest wins; ties keep this list's order, which
        // is the original precedence. Adding a marker can only add candidates, so
        // the best score can never fall.
        addPair(gHigh && hHigh, gPastMid, hPastMid, PossibleRootCauseCategory.CATEGORY_71085, PossibleRootCauseCategory.CATEGORY_71040, PossibleRootCauseCategory.CATEGORY_71045);
        addPair(gHigh && tHigh, gPastMid, tPastMid, PossibleRootCauseCategory.CATEGORY_71084, PossibleRootCauseCategory.CATEGORY_71041, PossibleRootCauseCategory.CATEGORY_71046);
        addPair(hHigh && tHigh, hPastMid, tPastMid, PossibleRootCauseCategory.CATEGORY_71083, PossibleRootCauseCategory.CATEGORY_71042, PossibleRootCauseCategory.CATEGORY_71047);
        addPair(gHigh && isOptHigh, gPastMid, isPastMidHigh, PossibleRootCauseCategory.CATEGORY_71068, PossibleRootCauseCategory.CATEGORY_71043, PossibleRootCauseCategory.CATEGORY_71048);
        addPair(hHigh && isOptHigh, hPastMid, isPastMidHigh, PossibleRootCauseCategory.CATEGORY_71067, PossibleRootCauseCategory.CATEGORY_71044, PossibleRootCauseCategory.CATEGORY_71049);
        // TG/HDL + insulin signal. Without this rung the two had no pair between
        // them, so the panel dropped to the TG/HDL single at 60/55 and the insulin
        // counted for nothing — the wrong weighting, since the insulin signal says
        // more about glucose handling than the lipid ratio does.
        addPair(tHigh && isOptHigh, tPastMid, isPastMidHigh, PossibleRootCauseCategory.CATEGORY_71069, PossibleRootCauseCategory.CATEGORY_71070, PossibleRootCauseCategory.CATEGORY_71071);
        const bestPair = bestOf(pairCandidates);
        if (bestPair) return this.setGaugeDriver(bestPair.score, bestPair.id);

        // ── 60% / 50% Possible — single above/below optimal, graded by midpoint
        // Past the lab↔optimal midpoint keeps 60%; only mildly off-optimal
        // steps down to 50% (both still Possible) when the rest of the panel
        // corroborates. Low-side singles grade at the low midpoint (avgMin) the
        // same way the high side grades at avgMax.
        // Same best-wins rule as the pairs, for the same reason: TG/HDL sits above
        // the insulin signal in this list, so a mild TG/HDL used to take the slot
        // from a past-midpoint insulin and drop the reading 60 → 55. Two low
        // markers hit it too, the milder one winning on list order alone.
        addSingle(gHigh, gPastMid, PossibleRootCauseCategory.CATEGORY_71066, PossibleRootCauseCategory.CATEGORY_71050);
        addSingle(hHigh, hPastMid, PossibleRootCauseCategory.CATEGORY_71065, PossibleRootCauseCategory.CATEGORY_71051);
        // Reaching here means TG/HDL is the sole driver: no pair fired and nothing
        // else is out of lab. When it is also past the midpoint (3.0 on the usual
        // 2.0-4.0 range, inside the 2.5-3.5 band the literature uses) and the rest
        // of the panel is measured and optimal, flag the non-glycemic differential
        // for the Insights panel. Deliberately NOT gated on the lab bound: a 3.5
        // ratio with an optimal glucose panel poses the same question as a 4.5 one.
        // The mild band below the midpoint is left alone, since at or under the
        // published cut-points there is little to explain. isOptHigh is excluded
        // for the same reason it is excluded from the 75% rung above: an insulin
        // signal over optimal contradicts the "glucose handling is intact" premise
        // the differential rests on.
        if (tHigh && tPastMid && corroboratedOptimal && !isOptHigh) {
            this.tgHdlPastMidpointDespiteOptimalPanel = true;
        }
        addSingle(tHigh, tPastMid, PossibleRootCauseCategory.CATEGORY_71064, PossibleRootCauseCategory.CATEGORY_71052);
        addSingle(isOptHigh, isPastMidHigh, PossibleRootCauseCategory.CATEGORY_71059, PossibleRootCauseCategory.CATEGORY_71053);
        // Low side is only consulted when nothing is above optimal: a panel with
        // findings in both directions reads on the high path by product decision.
        if (!singleCandidates.length) {
            addSingle(gLow, gLowPastMid, PossibleRootCauseCategory.CATEGORY_71063, PossibleRootCauseCategory.CATEGORY_71054);
            addSingle(hLow, hLowPastMid, PossibleRootCauseCategory.CATEGORY_71062, PossibleRootCauseCategory.CATEGORY_71055);
            addSingle(insulinLow, insulinLowPastMid, PossibleRootCauseCategory.CATEGORY_71061, PossibleRootCauseCategory.CATEGORY_71056);
            addSingle(cpLow, cpLowPastMid, PossibleRootCauseCategory.CATEGORY_71060, PossibleRootCauseCategory.CATEGORY_71057);
        }
        const bestSingle = bestOf(singleCandidates);
        if (bestSingle) return this.setGaugeDriver(bestSingle.score, bestSingle.id);

        // ── Less Likely tier — confidence ladder ──────────────────────────
        // By the time we reach this point every measured marker is in its
        // optimal range (the 60% / 80% / 85% / 95% / 99% tiers would have
        // fired otherwise). The likelihood now scales with how much primary
        // evidence the practitioner has gathered. Insulin and C-peptide
        // optimal don't push the confidence lower per the product decision —
        // they only show up when no primary marker is measured at all.
        const gtCount = (hasGlucose ? 1 : 0) + (hasHbA1c ? 1 : 0) + (hasTgHdl ? 1 : 0);
        if (gtCount === 3) {
            return this.setGaugeDriver(5, PossibleRootCauseCategory.CATEGORY_71005);
        }
        if (gtCount === 2) {
            if (hasGlucose && hasHbA1c) return this.setGaugeDriver(15, PossibleRootCauseCategory.CATEGORY_71015);
            if (hasGlucose && hasTgHdl) return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71021);
            /* hasHbA1c && hasTgHdl */  return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71022);
        }
        if (gtCount === 1) {
            if (hasTgHdl)   return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71025);
            if (hasHbA1c)   return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71024);
            /* hasGlucose */ return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71023);
        }
        // No primary marker measured — Insulin or C-peptide must be the one
        // present (the early-return guard above requires at least one of the
        // five markers).
        return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71020);
    }

    private setGaugeDriver(probability: number, cat: PossibleRootCauseCategory) {
        this.glucoseRegulationProbability = probability;
        this.possibleRootCauseCategories.push(cat);
    }

    calculateGlucoseRegulationLikelihoodCategory() {
        const p = this.glucoseRegulationProbability;
        if (p === undefined || p < 0) return;
        if (p >= 90) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.HIGHLY_LIKELY;
        } else if (p >= 70) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.LIKELY;
        } else if (p >= 50) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.POSSIBLE;
        } else {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.LESS_LIKELY;
        }
    }

    private addGlucosePatternCategory(category: PossibleRootCauseCategory) {
        if (!this.possibleRootCauseCategories.includes(category)) {
            this.possibleRootCauseCategories.push(category);
        }
    }

    private markerHasValue(marker?: any): boolean {
        return !!marker && !!marker.hasValue && marker.hasValue();
    }

    private isMarkerHigh(marker?: any): boolean {
        if (!this.markerHasValue(marker)) return false;
        return marker.isGreaterThanLab?.() || marker.isGreaterThanOptimal?.() || false;
    }

    private isMarkerLow(marker?: any): boolean {
        if (!this.markerHasValue(marker)) return false;
        return marker.isLessThanLab?.() || marker.isStricklyLessThanOptimal?.() || marker.isLessThanOptimal?.() || false;
    }

    private isMarkerOptimal(marker?: any): boolean {
        return this.markerHasValue(marker) && !this.isMarkerHigh(marker) && !this.isMarkerLow(marker);
    }

    calculateInsights() {
        const ba = this.ba;

        // ── Marker presence ────────────────────────────────────────────────────
        const hasGlucose = this.markerHasValue(ba.glucose);
        const hasHbA1c   = this.markerHasValue(ba.hemoglobinA1c);
        const hasInsulin = this.markerHasValue(ba.insulin);
        const hasCp      = this.markerHasValue(ba.cpeptide);

        // ── Glucose / HbA1c states ─────────────────────────────────────────────
        const glucoseHigh = hasGlucose && this.isMarkerHigh(ba.glucose);
        const glucoseLow  = hasGlucose && this.isMarkerLow(ba.glucose);
        const glucoseOpt  = hasGlucose && this.isMarkerOptimal(ba.glucose);
        const hbA1cHigh   = hasHbA1c  && this.isMarkerHigh(ba.hemoglobinA1c);

        // ── Insulin / C-peptide states ─────────────────────────────────────────
        const insulinHigh = hasInsulin && this.isMarkerHigh(ba.insulin);
        const insulinLow  = hasInsulin && this.isMarkerLow(ba.insulin);
        const insulinOpt  = hasInsulin && this.isMarkerOptimal(ba.insulin);
        const cpHigh      = hasCp     && this.isMarkerHigh(ba.cpeptide);
        const cpLow       = hasCp     && this.isMarkerLow(ba.cpeptide);
        const cpOpt       = hasCp     && this.isMarkerOptimal(ba.cpeptide);

        // Discrepant insulin / C-peptide (one ↑, the other ↓). Physiologically
        // co-secreted, so this combination is exceedingly rare (~1 in 15k tests)
        // and typically indicates lab error. When detected, primary patterns
        // are routed as if BOTH insulin and C-peptide were absent, and a
        // single add-on (22.x / CATEGORY_73250) renders below.
        const mixedInsulinCpDiscrepancy =
            (insulinHigh && cpLow) || (cpHigh && insulinLow);

        // ── Lipid offset: 0 = none, 50 = TG/HDL↑, 100 = TG↑+TG/HDL not high ──
        const tgHdlHigh  = this.markerHasValue(ba.triglyceridesHDLRatio) && this.isMarkerHigh(ba.triglyceridesHDLRatio);
        const tgHigh     = this.markerHasValue(ba.triglycerides) && this.isMarkerHigh(ba.triglycerides);
        const lipidOffset = tgHdlHigh ? 50 : (tgHigh ? 100 : 0);

        // ── Insulin variant (0–45, step 5) for main high-glucose patterns ──────
        // When insulin and C-peptide disagree (one ↑, the other ↓), treat
        // both as absent for routing — the 22.x add-on (CATEGORY_73250)
        // flags the discrepancy separately below.
        const insulinVariant = (() => {
            if (mixedInsulinCpDiscrepancy) return 0;
            if (!hasInsulin && !hasCp) return 0;
            if (hasInsulin && !hasCp) {
                if (insulinHigh) return 5;
                if (insulinOpt)  return 20;
                return 35; // insulinLow
            }
            if (!hasInsulin && hasCp) {
                if (cpHigh) return 10;
                if (cpOpt)  return 25;
                return 40; // cpLow
            }
            // Both present.
            // high takes priority, then both-low, then single-low
            // (the opt counterpart is treated as silent), then both-opt.
            if (insulinHigh || cpHigh) return 15;
            if (insulinLow  && cpLow)  return 45;
            if (insulinLow)            return 35; // insulin↓, cp = opt
            if (cpLow)                 return 40; // cp↓, insulin = opt
            return 30; // both optimal
        })();

        // ── Main pattern: groupBase + lipidOffset + insulinVariant ─────────────
        if ((glucoseHigh || hbA1cHigh) && insulinVariant >= 0) {
            let groupBase: number;
            if      (glucoseHigh && hbA1cHigh)    groupBase = 72800;
            else if (glucoseHigh && !hasHbA1c)    groupBase = 72000;
            else if (glucoseHigh)                  groupBase = 72400; // HbA1c present but ≤ optimal
            else if (hbA1cHigh && !hasGlucose)    groupBase = 72200;
            else                                   groupBase = 72600; // Glucose present but ≤ optimal
            const catId = groupBase + lipidOffset + insulinVariant;
            this.addGlucosePatternCategory((PossibleRootCauseCategory as any)['CATEGORY_' + catId]);
        }

        // ── Low glucose primary patterns (19.1, 19.3–19.8: 73060, 73070–73083) ─
        // ── plus lipid variants (19.9–19.22: 73084–73097) ──────────────────────
        // Each pattern's conditions are mutually exclusive via insulin/cp
        // presence and lipid state, so only one fires per bloodwork.
        // "≤ Opt" in sheet = has value AND not high. "blank" = !hasValue.
        // Lipid groups: TG/HDL high → 73084 base; TG high (TG/HDL ≤ opt) → 73091 base.
        if (glucoseLow && !hbA1cHigh) {
            const glucoseBelowLab = hasGlucose && (ba.glucose as any).isLessThanLab?.() === true;
            const insulinLeOpt = hasInsulin && !insulinHigh; // Low or Optimal
            const cpLeOpt      = hasCp      && !cpHigh;

            const tgLow      = tgHigh && !tgHdlHigh;
            const lipidBase  = tgHdlHigh ? 73084 : (tgLow ? 73091 : null);

            // configOffset selects the insulin/cp variant within each lipid
            // sub-family. Same ordering as the non-lipid 19.x parents.
            //   0 = no insulin/cp, 1 = insulin↑, 2 = cp↑, 3 = both↑,
            //   4 = insulin≤opt, 5 = cp≤opt, 6 = both≤opt
            const configOffset = (() => {
                if (!hasInsulin && !hasCp) return 0;
                if (insulinHigh && !hasCp) return 1;
                if (!hasInsulin && cpHigh) return 2;
                if (insulinHigh && cpHigh) return 3;
                if (insulinLeOpt && !hasCp) return 4;
                if (!hasInsulin && cpLeOpt) return 5;
                if (insulinLeOpt && cpLeOpt) return 6;
                return -1; // mixed (e.g. insulin↑ + cp≤opt) has no sheet pattern
            })();
            const nonLipidIds = [73060, 73070, 73075, 73080, 73081, 73082, 73083];

            if (configOffset >= 0) {
                const categoryId = lipidBase !== null
                    ? lipidBase + configOffset
                    : nonLipidIds[configOffset];
                this.addGlucosePatternCategory(
                    (PossibleRootCauseCategory as any)['CATEGORY_' + categoryId],
                );
            }

            // 19.2 — "Additional Evaluation Warranted" severity add-on.
            // Fires whenever glucose is below lab range, regardless of
            // insulin/cp state. Emitted LAST so it renders below the more
            // specific pattern in the UI.
            if (glucoseBelowLab) {
                this.addGlucosePatternCategory(
                    (PossibleRootCauseCategory as any)['CATEGORY_73065'],
                );
            }
        }

        // ── Optimal glucose + high insulin/cp (21.x: 73200–73240) ─────────────
        // Skip when insulin/cp are discrepant — they're treated as absent for
        // routing, so the "high insulin/cp" gate doesn't apply. The 22.x
        // add-on (CATEGORY_73250) flags the discrepancy below.
        if (glucoseOpt && !hbA1cHigh && (insulinHigh || cpHigh) && !mixedInsulinCpDiscrepancy) {
            const lipidOffset21    = tgHdlHigh ? 15 : (tgHigh ? 30 : 0);
            const insulinVariant21 = (insulinHigh && cpHigh) ? 10 : (insulinHigh ? 0 : 5);
            this.addGlucosePatternCategory((PossibleRootCauseCategory as any)['CATEGORY_' + (73200 + lipidOffset21 + insulinVariant21)]);
        }

        // ── Add-ons (fire independently of primary pattern) ───────────────────
        const anyHigh = glucoseHigh || hbA1cHigh;

        // 16.1: Elevated LDL Compounding Glucose-Related Cardiometabolic Risk
        if (anyHigh && this.isMarkerHigh(ba.ldl)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73000);
        }

        // 17.1: Liver Stress May Be Contributing to Glucose Dysregulation
        if (anyHigh && this.isMarkerHigh(ba.alt)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73010);
        }

        // 18.1: Stress Hormone Influence (Cortisol AM high)
        if (anyHigh && this.isMarkerHigh(ba.cortisolAM)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73020);
        }

        // 18.2: Acute Stress Pattern (NLR high + hs-CRP optimal or no value)
        if (anyHigh && this.isMarkerHigh(ba.nlr) && !this.isMarkerHigh(ba.hsCRP) && !this.isMarkerLow(ba.hsCRP)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73025);
        }

        // 18.3: Protein Metabolism Influence (CO2 low + BUN high)
        if (anyHigh && this.isMarkerLow(ba.carbonDioxide) && this.isMarkerHigh(ba.bun)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73030);
        }

        // 18.4–18.6: Inadequate Pancreatic Response — insulin & C-peptide low,
        // K and/or Ca strictly below lab range. K+Ca variant takes precedence.
        if (anyHigh && insulinLow && cpLow) {
            const kLabLow  = !!ba.potassium?.isLessThanLab?.();
            const caLabLow = !!ba.calcium?.isLessThanLab?.();
            if      (kLabLow && caLabLow) this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73045);
            else if (kLabLow)             this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73035);
            else if (caLabLow)            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73040);
        }

        // 18.7–18.10: Low Amylase pancreatic context — amylase low with elevated
        // glucose or HbA1c. Combined low insulin + low C-peptide variant (18.10)
        // takes precedence over the single-marker variants (18.8 / 18.9),
        // which take precedence over the amylase-only base pattern (18.7).
        if (anyHigh && this.isMarkerLow(ba.amylase)) {
            if      (insulinLow && cpLow) this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73059);
            else if (insulinLow)          this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73055);
            else if (cpLow)               this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73057);
            else                          this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73050);
        }

        // 20.1: Protein-Influenced Low Glucose
        if (glucoseLow && this.isMarkerHigh(ba.bun) &&
            (!this.markerHasValue(ba.creatinine) || !this.isMarkerHigh(ba.creatinine))) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73100);
        }

        // 20.2: Low Cortisol-Influenced Glucose Pattern
        if (glucoseLow && (this.isMarkerLow(ba.cortisolAM) || this.isMarkerLow(ba.cortisolPM))) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73105);
        }

        // 20.3–20.6: B6 / ALT / AST Low Glucose Add-ons
        if (glucoseLow) {
            const b6Low  = this.markerHasValue(ba.vitaminB6) && this.isMarkerLow(ba.vitaminB6);
            const altLow = this.isMarkerLow(ba.alt);
            const astLow = this.isMarkerLow(ba.ast);
            if      (b6Low)            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73110);
            else if (altLow && astLow) this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73125);
            else if (altLow)           this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73115);
            else if (astLow)           this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73120);
        }

        // 22.1: Insulin / C-peptide Discrepancy add-on. Fires whenever the
        // two markers disagree (one ↑, the other ↓) regardless of glucose
        // state. Both markers were already treated as absent in primary-
        // pattern routing above; this add-on surfaces the discrepancy so the
        // practitioner can investigate (retest, lab error, unusual physiology).
        if (mixedInsulinCpDiscrepancy) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73250);
        }

        // ── Existing single-marker categories ─────────────────────────────────
        if (this.ba.glucose.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6000);
        } else if (this.ba.glucose.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6010);
        }

        if (this.ba.hemoglobinA1c.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6020);
        } else if (this.ba.hemoglobinA1c.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6030);
        }

        if (this.ba.insulin.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6040);
        } else if (this.ba.insulin.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6050);
        }

        if (this.ba.cpeptide.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6060);
        } else if (this.ba.cpeptide.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6070);
        }

        if (
          this.ba.triglycerides.isGreaterThanOptimal() &&
          this.ba.triglyceridesHDLRatio.isLessThanOrIsOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6075,
          );
        } else if (
          this.ba.triglycerides.isGreaterThanOptimal() &&
          this.ba.triglyceridesHDLRatio.isGreaterThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6076,
          );
        } else if (
          this.ba.triglycerides.isLessThanOrIsOptimal() &&
          this.ba.triglyceridesHDLRatio.isGreaterThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6077,
          );
        }

        if (this.ba.amylase.isGreaterThanOptimal()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6080);
        }

        if (this.ba.amylase.isGreaterThanLab()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6085);
        }

        if (this.ba.amylase.isLessThanOptimal()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6090);
        }

        if (this.ba.amylase.isLessThanLab()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6091);
        }

    }
}